/**
 * Single source of truth for the "Incomplete Loads" KPI card <-> Status
 * filter relationship on the On-Time Dispatch dashboard.
 *
 * Root cause this module fixes: the Incomplete Loads KPI card previously
 * drove its highlighted state from a standalone `incompleteLoadsFocused`
 * boolean that was toggled independently of the Status filter. That boolean
 * could (and did) drift out of sync with the applied Status filter — e.g.
 * selecting "Incomplete" in the Status dropdown and clicking Apply did not
 * highlight the card, and the card's own click-to-filter shortcut did not
 * update the Status dropdown or survive a tab switch consistently.
 *
 * The fix: derive the highlight *only* from the APPLIED Status filter
 * (never from draft/unapplied selections, never from a separate flag), and
 * route the card's click-to-filter shortcut through the same Status filter
 * state. Both OnTimeDispatchDashboard and ExecutiveOverviewTab import these
 * helpers so there is exactly one implementation to keep in sync.
 */

export interface StatusFilterItem {
  id: string | number;
  displayText: string;
}

const INCOMPLETE_LABEL = "incomplete";

const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

/** Canonical "Incomplete" Status filter item (matches LOAD_STATUS_OPTIONS). */
export const INCOMPLETE_STATUS_FILTER_ITEM: StatusFilterItem = {
  id: "Incomplete",
  displayText: "Incomplete",
};

/**
 * True when the given (APPLIED) Status filter selection contains
 * "Incomplete". Supports multi-select: any entry matching "Incomplete"
 * (case-insensitive) causes this to return true, regardless of what else
 * is selected alongside it.
 */
export const isIncompleteStatusApplied = (
  selectedStatuses: Array<Pick<StatusFilterItem, "id">> | undefined | null
): boolean =>
  !!selectedStatuses?.some((item) => normalize(item.id) === INCOMPLETE_LABEL);

/**
 * Computes the next Status filter selection when the Incomplete Loads KPI
 * card is pressed. Acts as a toggle:
 *  - If Incomplete is not currently applied, drill in to Incomplete-only.
 *  - If Incomplete is currently applied, clear the Status filter.
 *
 * The caller is expected to apply the result to BOTH the draft Status
 * selection and the applied filters snapshot atomically, so the Status
 * dropdown, the card highlight, and the outgoing API payload never diverge.
 */
export const toggleIncompleteStatusFilter = (
  currentSelectedStatuses: Array<Pick<StatusFilterItem, "id">> | undefined | null
): StatusFilterItem[] =>
  isIncompleteStatusApplied(currentSelectedStatuses)
    ? []
    : [INCOMPLETE_STATUS_FILTER_ITEM];
